import React, { useEffect, useState } from 'react';
import { Trash2, Activity, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { apiClient } from '@/services/api';

interface AuditorAccess {
  id: number;
  auditor_name: string;
  auditor_email: string;
  granted_at: string;
  last_activity: string;
  is_active: boolean;
  activity_count: number;
  granted_by_name?: string;
}

export const AuditorAccessList: React.FC = () => {
  const [auditors, setAuditors] = useState<AuditorAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<number | null>(null);

  const fetchAuditors = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.getAuditorAccessList();

      if (data.success) {
        setAuditors(data.data || []);
      } else {
        setError('Failed to load auditors');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditors();
  }, []);

  const handleRevoke = async (accessId: number, auditorName: string) => {
    if (!confirm(`Are you sure you want to revoke ${auditorName}'s access? They will immediately lose access to your business data.`)) {
      return;
    }

    setRevokingId(accessId);

    try {
      const data = await apiClient.revokeAuditorAccess(accessId);

      if (data.success) {
        // Remove from list
        setAuditors(prev => prev.filter(a => a.id !== accessId));
      } else {
        alert('Failed to revoke access');
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (auditors.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium mb-1">No auditors have access yet</p>
        <p className="text-gray-500 text-sm">
          Invite an auditor to grant them access to your GST reports
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">
          {auditors.length} auditor{auditors.length !== 1 ? 's' : ''} with access
        </p>
        <button
          onClick={fetchAuditors}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Auditor List */}
      {auditors.map((auditor) => (
        <div
          key={auditor.id}
          className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
        >
          <div className="flex items-start justify-between">
            {/* Auditor Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-gray-900">{auditor.auditor_name}</h4>
                {auditor.is_active ? (
                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                    Active
                  </span>
                ) : (
                  <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                    Revoked
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-3">{auditor.auditor_email}</p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div>
                  <p className="text-gray-500 mb-0.5">Access Granted</p>
                  <p className="font-medium text-gray-900">
                    {formatDistanceToNow(new Date(auditor.granted_at), { addSuffix: true })}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 mb-0.5">Last Activity</p>
                  <p className="font-medium text-gray-900">
                    {auditor.last_activity
                      ? formatDistanceToNow(new Date(auditor.last_activity), { addSuffix: true })
                      : 'Never'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 mb-0.5">Total Actions</p>
                  <p className="font-medium text-gray-900 flex items-center gap-1">
                    <Activity className="w-3 h-3" />
                    {auditor.activity_count || 0}
                  </p>
                </div>
              </div>

              {auditor.granted_by_name && (
                <p className="text-xs text-gray-500 mt-2">
                  Granted by: {auditor.granted_by_name}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="ml-4 flex gap-2">
              {/* <button
                onClick={() => {}}
                className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                View Activity
              </button> */}
              {auditor.is_active && (
                <button
                  onClick={() => handleRevoke(auditor.id, auditor.auditor_name)}
                  disabled={revokingId === auditor.id}
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                >
                  {revokingId === auditor.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Revoke
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
